import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Target, AlertTriangle, CheckCircle, ArrowRight, Info } from 'lucide-react';
import NoisePatternScanner from '@/components/council/NoisePatternScanner';
import BulkIssueEnrichment from '@/components/council/IssueEnrichmentPanel';
import RejectedIssuesSection from '@/components/council/RejectedIssuesSection';

export default function Phase1StateOfField({ issues, missions, officers, lastSession, onProceed, allIssues }) {
  const activeMissions = missions.filter((m) => m.status === 'active').
  sort((a, b) => {
    const flagOrder = { red: 0, yellow: 1, green: 2 };
    return (flagOrder[a.status_flag] || 2) - (flagOrder[b.status_flag] || 2);
  }).
  slice(0, 3);

  const pendingTriage = issues.filter((i) => !i.classification_gate1 || i.classification_gate1 === 'pending');
  const pendingDensity = issues.filter((i) => i.classification_gate1 === 'signal' && (!i.classification_gate2 || i.classification_gate2 === 'pending'));

  // Action items from last session
  const lastSessionActions = lastSession?.action_items?.filter((a) => a.status !== 'done') || [];

  // Previous resolution status
  const lastReviewedIssues = lastSession?.issues_reviewed || [];
  const issuesWithResolution = issues.filter((i) =>
  lastReviewedIssues.includes(i.id) && i.resolution_owner
  );

  // Noise archive pattern detection
  const archivedNoise = issues.filter((i) => i.classification_gate1 === 'noise');
  const tagCounts = {};
  archivedNoise.forEach((issue) => {
    [...(issue.tagged_officers || []), ...(issue.tagged_capabilities || []), ...(issue.tagged_policies || [])].forEach((tag) => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });
  const patternAlerts = Object.entries(tagCounts).filter(([, count]) => count >= 3);

  const flagColors = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500' };

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white">Phase 1: State of the Field</h2>
        <p className="text-sm text-slate-300">5-10 minutes — Review context before diving into the queue</p>
      </div>

      {/* Commander's Intent */}
      <Card className="glass-panel border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-indigo-400 flex items-center gap-2">
            <Target className="w-4 h-4" /> Commander's Intent — Active Missions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {activeMissions.length === 0 ?
          <p className="text-xs text-slate-400">No active missions</p> :
          activeMissions.map((m) =>
          <div key={m.id} className="bg-slate-50 p-2 rounded flex items-center gap-3">
              <div className={`w-2 h-8 rounded-full ${flagColors[m.status_flag] || 'bg-slate-500'}`} />
              <div className="flex-1">
                <p className="text-slate-700 text-sm font-medium">{m.mission_code && `[${m.mission_code}] `}{m.title}</p>
                <p className="text-xs text-slate-400">{m.game_mode?.replace('_', ' ')}</p>
              </div>
              <Badge className="bg-amber-500/20 text-amber-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">
                {m.status_flag}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Queue Statistics */}
      <Card className="glass-panel border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-amber-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Queue Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 p-3 text-center rounded-lg">
              <p className="text-2xl font-bold text-red-400">{pendingTriage.length}</p>
              <p className="text-xs text-slate-400">Pending Triage</p>
            </div>
            <div className="bg-slate-50 p-3 text-center rounded-lg">
              <p className="text-2xl font-bold text-amber-300">{pendingDensity.length}</p>
              <p className="text-xs text-slate-400">Awaiting Density</p>
            </div>
            <div className="bg-slate-50 p-3 text-center rounded-lg">
              <p className="text-2xl font-bold text-cyan-300">{lastSessionActions.length}</p>
              <p className="text-xs text-slate-400">Open Action Items</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Previous Resolution Status */}
      {issuesWithResolution.length > 0 &&
      <Card className="glass-panel border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Previous Resolutions Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {issuesWithResolution.slice(0, 5).map((issue) => {
            const owner = officers.find((o) => o.user_email === issue.resolution_owner);
            return (
              <div key={issue.id} className="flex items-center justify-between p-2 rounded bg-slate-800/40">
                  <div className="flex-1">
                    <p className="text-[10px] text-slate-500 font-mono select-text cursor-text">{issue.id}</p>
                    <p className="text-sm text-slate-200">{issue.title}</p>
                    <p className="text-xs text-slate-400">Owner: {owner?.callsign || issue.resolution_owner}</p>
                  </div>
                  <Badge className={issue.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}>
                    {issue.status?.replace('_', ' ')}
                  </Badge>
                </div>);

          })}
          </CardContent>
        </Card>
      }

      {/* Noise Archive Pattern Alerts */}
      {patternAlerts.length > 0 &&
      <Card className="glass-panel border-red-500/30 border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-400 flex items-center gap-2">
              <Info className="w-4 h-4" /> Noise Archive Pattern Alert
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {patternAlerts.map(([tag, count]) =>
          <div key={tag} className="flex items-center gap-2 p-2 rounded bg-red-950/20">
                <AlertTriangle className="w-3 h-3 text-red-400" />
                <p className="text-xs text-red-300">
                  <span className="font-semibold">{count}</span> archived noise items related to <span className="font-semibold text-red-200">{tag}</span>
                </p>
              </div>
          )}
          </CardContent>
        </Card>
      }
      {/* Rejected Issues & Appeals */}
      <RejectedIssuesSection issues={allIssues || issues} officers={officers} />

      {/* AI Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NoisePatternScanner />
        <BulkIssueEnrichment issues={issues} />
      </div>


      <Button onClick={onProceed} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-base">
        Proceed to Triage <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>);

}